import { Markdown } from "@repo/ui/components/markdown";
import { Badge } from "@repo/ui/components/ui/badge";
import { Separator } from "@repo/ui/components/ui/separator";
import { cn } from "@repo/ui/lib/utils";
import { useStore } from "@tanstack/react-form";
import type { LucideIcon } from "lucide-react";
import { Children, cloneElement, isValidElement, type ReactElement, type ReactNode } from "react";
import { formatDate } from "@/shared/lib/date-formats";
import { renderStatusItem } from "@/shared/status/status-picker-utils";
import { statusLabel, useCatalogDefinition } from "./catalog-definition-context";
import type { ShellForm } from "./use-shell-form";
import { VersionHistory } from "./version-history";

/**
 * Minimal `asChild`: render the caller's element instead of ours, merging our
 * className onto it. Local rather than a Slot dependency — the provider app
 * does not depend on Base UI or Radix directly, and this is the whole contract.
 */
function asChildElement(children: ReactNode, className: string): ReactElement {
  const child = Children.only(children) as ReactElement<{ className?: string }>;
  if (!isValidElement(child)) {
    throw new Error("CatalogDefinition: `asChild` requires a single React element child.");
  }
  return cloneElement(child, { className: cn(className, child.props.className) });
}

// ---------------------------------------------------------------------------
// Layout slots
// ---------------------------------------------------------------------------

/**
 * The sticky action bar: kind badge, status, version, `Updated …` and the
 * version history button on the left, the kind's own actions on the right.
 *
 * The left block is the one that had been retyped per kind, which is how the
 * status badge ended up below the title on procedures and top-right on exams,
 * spelled "Inactive" in one place and "retired" in the other. It is rendered
 * here, once, from `shell`. History is part of it — it is the lineage behind
 * the version already printed, not one of the kind's actions.
 *
 * `lead` sits after the status, for a kind whose body is a full-bleed canvas
 * and cannot spare a document header for its name.
 */
function Toolbar({ lead, children }: { lead?: ReactNode; children?: ReactNode }) {
  const { state } = useCatalogDefinition("Toolbar");
  const { shell } = state;

  return (
    <div className="sticky top-0 z-20 flex shrink-0 items-center justify-between gap-3 border-b bg-background px-4 py-3">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Badge variant="default">{shell.kindLabel}</Badge>
        <StatusBadge />
        {lead}
        {shell.lastUpdated && (
          <span className="text-sm text-muted-foreground">Updated {formatDate(shell.lastUpdated)}</span>
        )}
        {/* The version badge IS the history trigger, so the bar does not print `v4` too. */}
        {state.versions && <VersionHistory />}
      </div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

/**
 * The one spelling of a definition's status: Active / Inactive / Draft, in one
 * position on every kind.
 *
 * When the page edits status in place it reads the live form value rather than
 * the mapped `shell.status`, so the badge and the picker below it are the same
 * value and cannot drift apart mid-edit.
 */
function StatusBadge() {
  const { state, form } = useCatalogDefinition("StatusBadge");
  return form ? <LiveStatusBadge form={form} /> : <Badge variant="outline">{statusLabel(state.shell.status)}</Badge>;
}

function LiveStatusBadge({ form }: { form: ShellForm }) {
  const status = useStore(form.form.store, (s) => s.values.status);
  return <Badge variant="outline">{statusLabel(status)}</Badge>;
}

/** Title, description and properties in the page's normal scroll flow. */
function Header({ children }: { children: ReactNode }) {
  useCatalogDefinition("Header");
  return (
    <div className="shrink-0 px-6 pt-6">
      <div className="mx-auto w-full max-w-(--form-max-width) space-y-3">
        {children}
        <Separator />
      </div>
    </div>
  );
}

/** The remainder — stats, then the kind's read view or builder. */
function Body({ children }: { children: ReactNode }) {
  useCatalogDefinition("Body");
  return (
    <div className="flex-1 px-6 pb-6">
      <div className="mx-auto w-full max-w-(--form-max-width) space-y-4 pt-4">{children}</div>
    </div>
  );
}

interface TextSlotProps {
  asChild?: boolean;
  children?: ReactNode;
  className?: string;
  placeholder?: string;
}

/** Editable when the page carries a shell form; a heading otherwise. */
function Title({ asChild, children, className, placeholder }: TextSlotProps) {
  const { state, form } = useCatalogDefinition("Title");

  if (form) {
    return (
      <form.form.AppField name="title">
        {(field) => (
          <field.InputField
            variant="inline"
            placeholder={placeholder ?? `${state.shell.kindLabel} title`}
            disabled={!state.canEdit}
          />
        )}
      </form.form.AppField>
    );
  }

  const base = cn("text-2xl font-semibold tracking-tight", className);
  if (asChild) return asChildElement(children, base);
  return <h1 className={base}>{children ?? state.shell.title}</h1>;
}

/** Editable when the page carries a shell form; rendered markdown otherwise, omitted when empty. */
function Description({ asChild, children, className, placeholder }: TextSlotProps) {
  const { state, form } = useCatalogDefinition("Description");

  if (form) {
    return (
      <form.form.AppField name="description">
        {(field) => (
          <field.RichTextField
            placeholder={placeholder ?? `Add a ${state.shell.kindLabel.toLowerCase()} description...`}
          />
        )}
      </form.form.AppField>
    );
  }

  const base = cn("text-sm text-muted-foreground", className);
  if (asChild) return asChildElement(children, base);
  if (children) return <div className={base}>{children}</div>;
  const text = state.shell.description;
  return text ? (
    <Markdown className={base} parseIncompleteMarkdown={false}>
      {text}
    </Markdown>
  ) : null;
}

/**
 * A pure layout row for the kind's own pickers. The context exposes no
 * per-kind setters — whatever goes in here binds to the kind's form, or to
 * the shell form via `StatusPicker`.
 */
function Properties({ children }: { children?: ReactNode }) {
  useCatalogDefinition("Properties");
  return <div className="flex min-h-7 flex-wrap items-center gap-2">{children}</div>;
}

/** The shell form's status control. Renders nothing when the page has no shell form. */
function StatusPicker() {
  const { state, form } = useCatalogDefinition("StatusPicker");
  if (!form) return null;
  return (
    <form.form.AppField name="status">
      {(field) => (
        <field.PropertyPickerListField
          options={form.statusOptions}
          renderItem={renderStatusItem}
          bare
          disabled={!state.canEdit}
        />
      )}
    </form.form.AppField>
  );
}

/** The muted "45 min · 3 sections" row above the content. */
function Stats({ children }: { children?: ReactNode }) {
  useCatalogDefinition("Stats");
  return <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">{children}</div>;
}

function Stat({ icon: Icon, label }: { icon?: LucideIcon; label: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      {Icon && <Icon className="size-4" />}
      {label}
    </span>
  );
}

/** The kind's read view or its builder. The shell is chrome; this is the rest. */
function Content({ children }: { children: ReactNode }) {
  useCatalogDefinition("Content");
  return <div className="space-y-4">{children}</div>;
}

// ---------------------------------------------------------------------------
// Mode branches
// ---------------------------------------------------------------------------

/**
 * Renders children only while reading — returns null otherwise, so a builder
 * placed under WhenEditing is never instantiated on a read page.
 */
function WhenReading({ children }: { children: ReactNode }) {
  const { state } = useCatalogDefinition("WhenReading");
  return state.mode === "read" ? children : null;
}

function WhenEditing({ children }: { children: ReactNode }) {
  const { state } = useCatalogDefinition("WhenEditing");
  return state.mode === "edit" ? children : null;
}

export {
  Body,
  Content,
  Description,
  Header,
  Properties,
  Stat,
  Stats,
  StatusBadge,
  StatusPicker,
  Title,
  Toolbar,
  WhenEditing,
  WhenReading,
};
